import { ConfirmDeleteDialog } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/ConfirmDeleteDialog';
import { useDeleteRotationsplanVorlage } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteRotationsplanVorlage/useDeleteRotationsplanVorlage';
import { toast } from '@/hooks/use-toast';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@e2n/ui/v2';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const TRANSLATION_PREFIX = 'domain.mitarbeiter.rotationsplan.vorlagen';

/**
 * Kebab-Menü einer Vorlage, aktuell nur mit "Löschen".
 */
export function VorlageActionMenu({
  vorlageId,
  triggerVariant,
  size,
  onDeleted,
}: {
  vorlageId: string;
  triggerVariant: 'ghost' | 'ghost-on-grey';
  size: 'small' | 'medium';
  onDeleted?: () => void;
}) {
  const { t } = useTranslation();
  const [deleteRotationsplanVorlage] = useDeleteRotationsplanVorlage();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleDelete() {
    setIsSubmitting(true);

    try {
      const result = await deleteRotationsplanVorlage({ vorlageId });

      if (!result.data?.mitarbeiter_deleteRotationsplanVorlage.success) {
        toast({
          variant: 'destructive',
          title: t(`${TRANSLATION_PREFIX}.deleteVorlage.error`),
        });
        return;
      }

      setIsDialogOpen(false);
      toast({ title: t(`${TRANSLATION_PREFIX}.deleteVorlage.success`) });
      onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      toast({
        variant: 'destructive',
        title: t(`${TRANSLATION_PREFIX}.deleteVorlage.error`),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={t(`${TRANSLATION_PREFIX}.mehrOptionen`)}
            iconOnly
            size={size}
            type="button"
            variant={triggerVariant}>
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-32" align="end">
          <DropdownMenuGroup>
            <DropdownMenuItem
              className="text-error-red-200"
              onClick={() => setIsDialogOpen(true)}>
              {t(`${TRANSLATION_PREFIX}.deleteVorlage.label`)}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDeleteDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title={t(`${TRANSLATION_PREFIX}.deleteVorlage.titel`)}
        description={t(`${TRANSLATION_PREFIX}.deleteVorlage.text`)}
        isSubmitting={isSubmitting}
        onConfirm={handleDelete}
      />
    </>
  );
}
